import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { StoreActionLoggableName } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { CONVERSATION_LIST_LOAD_MORE_PAGE_SIZE, CONVERSATION_LIST_MAX_SIZE } from './constants';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { createStoreError } from './createStoreError';
import { getProcessedConversationChannels } from './getProcessedConversationChannels';
import { isEmptyConversation } from './isEmptyConversation';
import { mergeConversationChannelTasks } from './mergeConversationChannelTasks';
import type { ChatStoreAction } from './types';

const runExperienceTrackerSafely = (
	stage: 'start' | 'succeed' | 'fail',
	callback: () => void,
	isKillSwitchEnabled: boolean,
): void => {
	if (isKillSwitchEnabled) {
		callback();
		return;
	}

	try {
		callback();
	} catch (error) {
		void addSentryBreadcrumb({
			message: 'loadMoreConversationChannelsData: experience tracking failed',
			data: {
				stage,
				error: error instanceof Error ? error.message : String(error),
			},
		});
	}
};

export const loadMoreConversationChannelsData: SafeAction<[]> = createSafeAction(
	StoreActionLoggableName.loadMoreConversationChannelsData,
	(): ChatStoreAction =>
		async (
			{ setState, getState },
			{ assistanceService, experienceTracker, unifiedTasksActions },
		) => {
			const isKillSwitchEnabled = fg('rovo_chat_view_all_error_handling_killswitch');
			const { conversationPagination, conversations: loadedConversations } = getState();
			const isSearchPaginationEnabled = fg('rovo_chat_conversation_search_pagination');
			if (
				conversationPagination.isLoadingMore ||
				!conversationPagination.hasMore ||
				(!conversationPagination.nextCursor && !isSearchPaginationEnabled)
			) {
				return;
			}

			let pageLimit = CONVERSATION_LIST_LOAD_MORE_PAGE_SIZE;
			if (isSearchPaginationEnabled) {
				const loadedCount = Object.keys(loadedConversations).filter(
					(id) => !isEmptyConversation(id),
				).length;
				if (loadedCount >= CONVERSATION_LIST_MAX_SIZE) {
					setState({
						conversationPagination: {
							...conversationPagination,
							nextCursor: null,
							hasMore: false,
							loadMoreError: null,
						},
					});
					return;
				}
				pageLimit = Math.min(pageLimit, CONVERSATION_LIST_MAX_SIZE - loadedCount);
			}

			setState({
				conversationPagination: {
					...conversationPagination,
					isLoadingMore: true,
					loadMoreError: null,
				},
			});

			runExperienceTrackerSafely(
				'start',
				() => {
					experienceTracker.start({
						id: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
						name: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
					});
				},
				isKillSwitchEnabled,
			);

			try {
				const conversationChannelsResponse = await assistanceService.getConversationChannels({
					limit: pageLimit,
					...(conversationPagination.nextCursor
						? { cursor: conversationPagination.nextCursor }
						: {}),
				});
				let channelsFromAPI = conversationChannelsResponse.items;
				const { conversations } = getState();
				if (isSearchPaginationEnabled) {
					const loadedIds = new Set(
						Object.keys(conversations).filter((id) => !isEmptyConversation(id)),
					);
					// Chats created during the request use capacity without being removed.
					channelsFromAPI = channelsFromAPI.filter((channel) => {
						if (loadedIds.has(channel.id) || isEmptyConversation(channel.id)) {
							return true;
						}
						if (loadedIds.size >= CONVERSATION_LIST_MAX_SIZE) {
							return false;
						}
						loadedIds.add(channel.id);
						return true;
					});
				}
				const processedChannels = getProcessedConversationChannels({
					channelsFromAPI,
					conversations,
				});

				mergeConversationChannelTasks(channelsFromAPI, unifiedTasksActions);
				const mergedConversations = { ...conversations, ...processedChannels };
				const isAtConversationLimit =
					isSearchPaginationEnabled &&
					Object.keys(mergedConversations).filter((id) => !isEmptyConversation(id)).length >=
						CONVERSATION_LIST_MAX_SIZE;

				if (
					isSearchPaginationEnabled &&
					!isAtConversationLimit &&
					conversationChannelsResponse.hasNext &&
					(!conversationChannelsResponse.cursor ||
						conversationChannelsResponse.cursor === conversationPagination.nextCursor)
				) {
					setState({
						conversations: mergedConversations,
					});
					throw new Error('Conversation pagination cursor did not advance');
				}

				setState({
					conversations: mergedConversations,
					conversationPagination: {
						...getState().conversationPagination,
						nextCursor: isAtConversationLimit ? null : conversationChannelsResponse.cursor,
						hasMore: !isAtConversationLimit && conversationChannelsResponse.hasNext,
						isLoadingMore: false,
						loadMoreError: null,
					},
				});

				runExperienceTrackerSafely(
					'succeed',
					() => {
						experienceTracker.succeed({
							name: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
						});
					},
					isKillSwitchEnabled,
				);
			} catch (error) {
				const setLoadMoreError = () =>
					setState({
						conversationPagination: {
							...getState().conversationPagination,
							isLoadingMore: false,
							loadMoreError: createStoreError(
								error,
								undefined,
								StoreActionLoggableName.loadMoreConversationChannelsData,
							),
						},
					});

				const failExperience = () =>
					runExperienceTrackerSafely(
						'fail',
						() => {
							experienceTracker.fail({
								teamName: 'Rovo Chat',
								name: ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS,
								error: error instanceof Error ? error : new Error(String(error)),
							});
						},
						isKillSwitchEnabled,
					);

				if (isKillSwitchEnabled) {
					failExperience();
					setLoadMoreError();
					return;
				}

				setLoadMoreError();
				failExperience();
			}
		},
);
